import { datadogRum as datadogRumOrigin } from '@datadog/browser-rum';
import ClarityOrigin from '@microsoft/clarity';
import { gql } from '@apollo/client';
import isSSR from '@shared/utils/isSSR';
import { getProjectTarget, select } from '@shared/constants/projectTarget';
import { omitDeep } from '@shared/utils/omitdeep';

const datadogRum = datadogRumOrigin;
const Clarity = ClarityOrigin;

const hasDatadogEnv =
  process.env.NEXT_PUBLIC_DATA_DOG_APPLICATION_ID && process.env.NEXT_PUBLIC_DATA_DOG_CLIENT_TOKEN;

const hasClarityEnv = process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID;

type InitAPMInput = {
  version: string;
  serviceName: string;
  env: string;
  sessionReplayRecording?: boolean;
};

export const initAPM = ({ env, version, serviceName, sessionReplayRecording = false }: InitAPMInput) => {
  if (isSSR) {
    return;
  }
  if (hasDatadogEnv) {
    // datadog
    datadogRum.init({
      applicationId: process.env.NEXT_PUBLIC_DATA_DOG_APPLICATION_ID as string,
      clientToken: process.env.NEXT_PUBLIC_DATA_DOG_CLIENT_TOKEN as string,
      site: 'datadoghq.com',
      sampleRate: 100,
      premiumSampleRate: 100,
      trackInteractions: true,
      allowedTracingOrigins: [
        /https:\/\/.*\.english\.management/,
        /https:\/\/.*\gwstage.english\.management/,
        ...(process.env.NEXT_PUBLIC_GRAPHQL_URL ? [new URL(process.env.NEXT_PUBLIC_GRAPHQL_URL).origin] : []),
      ],
      service:
        serviceName +
        select({
          hub: '',
          mildang: '',
          schoolpt: getProjectTarget(),
        }),
      version: version,
      env: env,
      beforeSend: (event, context) => {
        // ResizeObserver loop 에러는 무해한 브라우저 노이즈 — Datadog에서 제외
        if (
          event.type === 'error' &&
          event.error.message.includes('ResizeObserver loop')
        ) {
          return false;
        }

        // collect a RUM resource's response headers
        if (
          event.type === 'resource' &&
          event.resource.type === 'fetch' &&
          (event.resource.url.endsWith('graphql') || event.resource.url === 'https://api.ai-dt.org/')
        ) {
          try {
            const { operationName, variables, query } = JSON.parse((context as any).requestInit.body);

            event.context = {
              ...event.context,
              'apollo.client.variablesStr': JSON.stringify(omitDeep(variables, ['password'])),
              'apollo.client.operationName': operationName,
              'apollo.client.query': query,
            };
          } catch (e) { }
        }
      },
      ...(sessionReplayRecording && {
        defaultPrivacyLevel: 'mask-user-input',
      }),
    });

    if (sessionReplayRecording) {
      // TODO: 부하 테스트로 잠시 주석 처리
      datadogRum.startSessionReplayRecording();
    }
  }

  if (hasClarityEnv) {
    // clarity
    Clarity.init(process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID as string);
  }
};

export const setGlobalContextProperty = (key: string, property: any) => {
  if (!hasDatadogEnv) {
    return;
  }
  datadogRum.setGlobalContextProperty(key, property);
};

export const removeUser = () => {
  if (!hasDatadogEnv) {
    return;
  }
  datadogRum.clearUser();
};

export const addAction = (name: string, context?: object | undefined) => {
  if (!hasDatadogEnv) {
    return;
  }
  datadogRum.addAction(name, context);
};

export const pageview = (name: string) => {
  if (!hasDatadogEnv) {
    return;
  }
  // datadogRum.startView(name);
};

export const addError = (error: unknown, context?: object | undefined) => {
  if (!hasDatadogEnv) {
    return;
  }
  datadogRum.addError(error, context);
};

gql`
  fragment Datadog_SetUser_User on User {
    id
    email
    name
  }
`;
